import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Home, ArrowLeft, MapPin, Phone, Mail } from 'lucide-react';

const NotFoundScreen = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-accent/5">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative flex items-center justify-center min-h-screen py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          {/* Moroccan-inspired decorative element */}
          <div className="mb-8">
            <div className="w-32 h-32 mx-auto mb-6 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary to-accent rounded-full opacity-20 animate-pulse"></div>
              <div className="absolute inset-4 bg-gradient-to-br from-accent to-primary rounded-full opacity-30 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
              <div className="absolute inset-8 bg-gradient-to-br from-primary to-accent rounded-full opacity-40 animate-pulse" style={{ animationDelay: '1s' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <MapPin className="w-12 h-12 text-primary" />
              </div>
            </div>
            <h1 className="text-8xl lg:text-9xl font-sofia font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-4">
              404
            </h1>
          </div>

          {/* Error Message */}
          <div className="mb-12">
            <h2 className="text-3xl lg:text-4xl font-sofia font-bold text-primary mb-6">
              {t('errors.notFound.title')}
            </h2>
            <p className="text-xl text-text-secondary font-sofia leading-relaxed max-w-2xl mx-auto">
              {t('errors.notFound.message')}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <Link
              to="/"
              className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-accent to-accent/90 text-white font-sofia font-semibold rounded-xl hover:from-accent/90 hover:to-accent transition-all duration-300 hover:scale-105 hover:shadow-xl shadow-lg"
            >
              <Home className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform duration-300" />
              {t('errors.notFound.backToHome')}
            </Link>

            <button
              onClick={() => window.history.back()}
              className="group inline-flex items-center px-8 py-4 border-2 border-primary text-primary font-sofia font-semibold rounded-xl hover:bg-primary hover:text-white transition-all duration-300 hover:scale-105 hover:shadow-xl"
            >
              <ArrowLeft className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform duration-300" />
              {t('errors.notFound.goBack')}
            </button>
          </div>

          {/* Helpful Links */}
          <div className="mb-12">
            <h3 className="text-xl font-sofia font-semibold text-primary mb-6">
              {t('errors.notFound.helpfulLinks')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
              <Link
                to="/about"
                className="group p-4 bg-white/80 backdrop-blur-sm rounded-xl border border-primary/10 hover:border-accent/30 hover:bg-white transition-all duration-300 hover:scale-105 hover:shadow-lg"
              >
                <div className="text-accent group-hover:text-accent/80 font-sofia font-medium transition-colors duration-200">
                  {t('nav.about')}
                </div>
              </Link>
              <Link
                to="/our-villas"
                className="group p-4 bg-white/80 backdrop-blur-sm rounded-xl border border-primary/10 hover:border-accent/30 hover:bg-white transition-all duration-300 hover:scale-105 hover:shadow-lg"
              >
                <div className="text-accent group-hover:text-accent/80 font-sofia font-medium transition-colors duration-200">
                  {t('nav.ourVillas')}
                </div>
              </Link>
              <Link
                to="/services"
                className="group p-4 bg-white/80 backdrop-blur-sm rounded-xl border border-primary/10 hover:border-accent/30 hover:bg-white transition-all duration-300 hover:scale-105 hover:shadow-lg"
              >
                <div className="text-accent group-hover:text-accent/80 font-sofia font-medium transition-colors duration-200">
                  {t('nav.services')}
                </div>
              </Link>
              <Link
                to="/contact"
                className="group p-4 bg-white/80 backdrop-blur-sm rounded-xl border border-primary/10 hover:border-accent/30 hover:bg-white transition-all duration-300 hover:scale-105 hover:shadow-lg"
              >
                <div className="text-accent group-hover:text-accent/80 font-sofia font-medium transition-colors duration-200">
                  {t('nav.contact')}
                </div>
              </Link>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white/90 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-primary/10">
            <h3 className="text-xl font-sofia font-semibold text-primary mb-6">
              {t('errors.notFound.needHelp')}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <a
                href={`mailto:${t('contact.email')}`}
                className="group flex items-center p-4 bg-gradient-to-r from-accent/5 to-accent/10 rounded-xl hover:from-accent/10 hover:to-accent/20 transition-all duration-300 hover:scale-105"
              >
                <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center mr-4 group-hover:bg-accent/30 transition-colors duration-300">
                  <Mail className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <div className="font-sofia font-medium text-primary">Email</div>
                  <div className="text-sm text-text-secondary">{t('contact.email')}</div>
                </div>
              </a>
              <a
                href={`tel:${t('contact.phone.morocco').replace(/\s/g, '')}`}
                className="group flex items-center p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl hover:from-primary/10 hover:to-primary/20 transition-all duration-300 hover:scale-105"
              >
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mr-4 group-hover:bg-primary/30 transition-colors duration-300">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="font-sofia font-medium text-primary">Phone</div>
                  <div className="text-sm text-text-secondary">{t('contact.phone.morocco')}</div>
                </div>
              </a>
            </div>
          </div>

        </div>
      </div>

    </div>
  );
};

export default NotFoundScreen;
